import React, { useEffect, useState, useMemo } from 'react'
import { Row, Col, Card, Button, Statistic, Typography, Avatar, Spin } from 'antd'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
  RobotOutlined,
  FileTextOutlined,
  RightOutlined,
  TeamOutlined,
  HeartOutlined,
  ExperimentOutlined,
  MedicineBoxOutlined,
  UserOutlined,
  ToolOutlined,
  SettingOutlined,
  SafetyOutlined,
  ScanOutlined,
  PictureOutlined,
  ThunderboltOutlined,
  BookOutlined,
} from '@ant-design/icons'
import { getHealthNews, getFeaturedExperts } from '../../api'
import type { HealthNews } from '../../api/dashboard'
import type { Expert } from '../../api/experts'
import { RootState } from '../../store'
import {
  collectUserRoles,
  isEditorialCommitteeMember,
} from '../../constants/editorialRoles'
import './Home.css'

const { Title, Paragraph, Text } = Typography

const Home: React.FC = () => {
  const navigate = useNavigate()
  const { isAuthenticated, roles, user } = useSelector(
    (state: RootState) => state.auth
  )
  const showIntelligentWriting = useMemo(() => {
    if (!isAuthenticated) return false
    return isEditorialCommitteeMember(collectUserRoles(roles, user?.role))
  }, [isAuthenticated, roles, user?.role])
  const [loading, setLoading] = useState(true) // 初始为true，显示加载状态
  const [news, setNews] = useState<HealthNews[]>([])
  const [experts, setExperts] = useState<Expert[]>([])

  useEffect(() => {
    // 加载数据
    const loadData = async () => {
      try {
        // 并行加载数据，每个API都有独立的错误处理
        const loadPromise = Promise.all([
          getHealthNews(3).catch(() => {
            // 静默处理错误，不显示警告（这些API可能不存在）
            return []
          }),
          getFeaturedExperts().catch(() => {
            // 静默处理错误，不显示警告（这些API可能不存在）
            return []
          })
        ])
        
        // 设置15秒超时（给API足够时间响应）
        const timeoutPromise = new Promise<[HealthNews[], Expert[]]>((resolve) => {
          setTimeout(() => {
            // 只在开发环境显示超时警告
            if (import.meta.env.DEV) {
              console.warn('数据加载超时，使用空数据')
            }
            resolve([[], []])
          }, 15000)
        })
        
        const [newsData, expertsData] = await Promise.race([
          loadPromise,
          timeoutPromise
        ])
        
        setNews(Array.isArray(newsData) ? newsData : [])
        setExperts(Array.isArray(expertsData) ? expertsData.slice(0, 4) : [])
      } catch (error) {
        // 静默处理错误，确保页面能正常显示
        setNews([])
        setExperts([])
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])


  const stats = [
    { title: '注册用户', value: 1234, suffix: '+', icon: <TeamOutlined />, color: '#FFFFFF' },
    { title: '教材数量', value: 56, suffix: '本', icon: <FileTextOutlined />, color: '#FFFFFF' },
    { title: '诊疗记录', value: 8901, suffix: '次', icon: <HeartOutlined />, color: '#FFFFFF' },
    { title: '工具使用', value: 12345, suffix: '次', icon: <ExperimentOutlined />, color: '#FFFFFF' },
  ]


  const partners = [
    '北京中医药大学',
    '上海中医药大学',
    '广州中医药大学',
    '成都中医药大学',
    '南京中医药大学',
    '天津中医药大学',
  ]

  if (loading) {
    return (
      <div className="home-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 64px)' }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div className="home-page">
      {/* Hero区域 */}
      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">岐黄智医</div>
            <Title level={1} className="hero-title">
              <span className="hero-title-line1">中医药人工智能</span>
              <span className="hero-title-line2">网络平台</span>
            </Title>
            <Paragraph className="hero-subtitle">
              传承千年智慧，创新现代医疗
              <br />
              融合传统中医文化与前沿AI技术
            </Paragraph>
            <div className="hero-actions">
              <Button 
                size="large" 
                className="hero-btn-secondary"
                onClick={() => navigate('/chat')}
              >
                立即使用
              </Button>
              <Button 
                size="large" 
                className="hero-btn-secondary"
                onClick={() => navigate('/diagnosis')}
              >
                了解更多
              </Button>
            </div>
          </div>
          <div className="hero-illustration">
            <div className="doctor-illustration">
              <div className="monitor-display">
                <div className="monitor-content">
                  <div className="organ-display"></div>
                  <div className="yin-yang-symbol">☯</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 智能诊疗模块 - 六者 */}
      <section className="diagnosis-section">
        <div className="section-container">
          <div className="section-header">
            <Title level={2} className="section-title">智能诊疗</Title>
            <Paragraph className="section-description">
              为医疗生态系统的六类核心角色提供专业智能诊疗服务
            </Paragraph>
          </div>
          <Row gutter={[24, 24]}>
            {[
              {
                key: 'doctor',
                title: '医者',
                icon: <TeamOutlined />,
                description: 'AI辅助诊断、多学科会诊、病例分析、学术交流',
                onClick: () => navigate('/diagnosis/doctor'),
              },
              {
                key: 'patient',
                title: '患者',
                icon: <UserOutlined />,
                description: '在线问诊、健康评估、会诊预约、健康档案管理',
                onClick: () => navigate('/diagnosis/patient'),
              },
              {
                key: 'pharmacist',
                title: '药者',
                icon: <MedicineBoxOutlined />,
                description: '用药指导、处方审核、药物相互作用检测、用药安全',
                onClick: () => navigate('/diagnosis/pharmacist'),
              },
              {
                key: 'technician',
                title: '械者',
                icon: <ToolOutlined />,
                description: '设备管理、影像分析、数据采集、技术维护',
                onClick: () => navigate('/diagnosis/technician'),
              },
              {
                key: 'admin',
                title: '管者',
                icon: <SettingOutlined />,
                description: '系统管理、数据统计、流程优化、权限配置',
                onClick: () => navigate('/diagnosis/admin'),
              },
              {
                key: 'legal',
                title: '法者',
                icon: <SafetyOutlined />,
                description: '合规审查、风险控制、法律咨询、纠纷处理',
                onClick: () => navigate('/diagnosis/legal'),
              },
            ].map((role) => (
              <Col xs={24} sm={12} lg={8} key={role.key}>
                <Card
                  className="knowledge-card"
                  hoverable
                  variant="borderless"
                  onClick={role.onClick}
                >
                  <div className="knowledge-icon-wrapper">
                    {role.icon}
                  </div>
                  <Title level={4} className="knowledge-title">{role.title}</Title>
                  <Paragraph className="knowledge-description">{role.description}</Paragraph>
                  <Button 
                    type="link" 
                    className="knowledge-link"
                    onClick={(e) => {
                      e.stopPropagation()
                      role.onClick()
                    }}
                  >
                    查看详情 <RightOutlined />
                  </Button>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* 中医药知识树模块 */}
      <section className="knowledge-section">
        <div className="section-container">
          <div className="section-header">
            <Title level={2} className="section-title">中医药知识树</Title>
            <Paragraph className="section-description">
              汇聚千年中医智慧，融合现代医学知识，构建完整的中医药知识体系
            </Paragraph>
          </div>
          <Row gutter={[24, 24]}>
            {[
              {
                key: 'intelligent-textbook',
                title: '智能中医药教材',
                icon: <BookOutlined />,
                description: '智能生成的中医药教材，涵盖本科、硕士、博士等各阶段教材内容',
                onClick: () => navigate('/textbook'),
              },
              {
                key: 'herbal-library',
                title: '中药库',
                icon: <MedicineBoxOutlined />,
                description: '中药学知识库，包含药材识别、药性功效、配伍禁忌、用法用量等',
                onClick: () => navigate('/knowledge/herbs'),
              },
              {
                key: 'prescription-library',
                title: '方剂库',
                icon: <FileTextOutlined />,
                description: '方剂学知识库，包含经典方剂、方剂组成、功效主治、临床应用等',
                onClick: () => navigate('/knowledge/prescriptions'),
              },
              {
                key: 'disease-library',
                title: '疾病库',
                icon: <HeartOutlined />,
                description: '疾病知识库，包含疾病诊断、辨证分型、治疗方案、预防保健等',
                onClick: () => navigate('/knowledge/diseases'),
              },
              {
                key: 'case-library',
                title: '病案库',
                icon: <FileTextOutlined />,
                description: '临床病案库，包含真实病例、诊疗过程、疗效分析、经验总结等',
                onClick: () => navigate('/knowledge/cases'),
              },
              ...(showIntelligentWriting
                ? [
                    {
                      key: 'intelligent-writing',
                      title: '智能写书',
                      icon: <RobotOutlined />,
                      description:
                        'AI自动生成高质量的中医药教材内容，支持批量编写和质量审核',
                      onClick: () => navigate('/textbook/writing'),
                    },
                  ]
                : []),
            ].map((module) => (
              <Col xs={24} sm={12} lg={8} xl={8} key={module.key}>
                <Card
                  className="knowledge-card"
                  hoverable
                  variant="borderless"
                  onClick={module.onClick}
                >
                  <div className="knowledge-icon-wrapper">
                    {module.icon}
                  </div>
                  <Title level={4} className="knowledge-title">{module.title}</Title>
                  <Paragraph className="knowledge-description">{module.description}</Paragraph>
                  <Button 
                    type="link" 
                    className="knowledge-link"
                    onClick={(e) => {
                      e.stopPropagation()
                      module.onClick()
                    }}
                  >
                    查看详情 <RightOutlined />
                  </Button>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* 智能工具集模块 */}
      <section className="tools-section">
        <div className="section-container">
          <div className="section-header">
            <Title level={2} className="section-title">智能工具集</Title>
            <Paragraph className="section-description">
              提供丰富的智能医疗工具，提升诊疗效率，优化医疗流程
            </Paragraph>
          </div>
          <Row gutter={[24, 24]}>
            {[
              {
                key: 'constitution',
                title: '体质识别',
                icon: <UserOutlined />,
                description: '中医体质辨识、体质分析、个性化调理建议',
                onClick: () => navigate('/tools/constitution'),
              },
              {
                key: 'prescription',
                title: '中药方剂识别',
                icon: <MedicineBoxOutlined />,
                description: '方剂识别、成分分析、功效评估、配伍建议',
                onClick: () => navigate('/tools/prescription'),
              },
              {
                key: 'imaging-diagnosis',
                title: '影像学诊断',
                icon: <PictureOutlined />,
                description: '包含病理诊断、心电图诊断、B超诊断、CT诊断、核磁诊断等医学影像智能分析功能',
                onClick: () => navigate('/tools/diagnosis'),
              },
            ].map((tool) => (
              <Col xs={24} sm={12} lg={8} xl={6} key={tool.key}>
                <Card
                  className="knowledge-card"
                  hoverable
                  variant="borderless"
                  onClick={tool.onClick}
                >
                  <div className="knowledge-icon-wrapper">
                    {tool.icon}
                  </div>
                  <Title level={4} className="knowledge-title">{tool.title}</Title>
                  <Paragraph className="knowledge-description">{tool.description}</Paragraph>
                  <Button 
                    type="link" 
                    className="knowledge-link"
                    onClick={(e) => {
                      e.stopPropagation()
                      tool.onClick()
                    }}
                  >
                    查看详情 <RightOutlined />
                  </Button>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* 最新资讯区 */}
      <section className="news-section">
        <div className="section-container">
          <div className="section-header-row">
            <Title level={2} className="section-title">最新资讯</Title>
            <Button type="link" className="view-all-link">
              查看全部 <RightOutlined />
            </Button>
          </div>
          <Row gutter={[24, 24]}>
            {news.length > 0 ? (
              news.map((item) => (
                <Col xs={24} sm={12} lg={8} key={item.id}>
                  <Card
                    className="news-card"
                    hoverable
                    cover={
                      <div className="news-image">
                        <div className="news-image-placeholder">
                          <FileTextOutlined style={{ fontSize: 48, color: '#4A90E2' }} />
                        </div>
                      </div>
                    }
                  >
                    <div className="news-content">
                      <Title level={5} className="news-title">{item.title}</Title>
                      <Text className="news-date">{item.publishDate}</Text>
                      <Paragraph className="news-summary" style={{ marginTop: 8, fontSize: 13, color: '#757575' }}>
                        {item.summary}
                      </Paragraph>
                    </div>
                  </Card>
                </Col>
              ))
            ) : (
              <Col span={24}>
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  暂无资讯数据
                </div>
              </Col>
            )}
          </Row>
        </div>
      </section>

      {/* 合作医院区 */}
      <section className="partners-section">
        <div className="section-container">
          <Title level={2} className="section-title">合作医院</Title>
          <div className="partners-grid">
            {partners.map((partner, index) => (
              <div key={index} className="partner-logo">
                <Text>{partner}</Text>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 专家团队区 */}
      <section className="experts-section">
        <div className="section-container">
          <div className="section-header-row">
            <Title level={2} className="section-title">我们的专家</Title>
            <Button type="link" className="view-all-link">
              查看全部 <RightOutlined />
            </Button>
          </div>
          <Row gutter={[24, 24]}>
            {experts.length > 0 ? (
              experts.map((expert) => (
                <Col xs={24} sm={12} lg={6} key={expert.id}>
                  <Card
                    className="expert-card"
                    hoverable
                    variant="borderless"
                    onClick={() => navigate(`/experts/${expert.id}`)}
                  >
                    <div className="expert-avatar-wrapper">
                      <Avatar 
                        size={120} 
                        src={expert.avatar} 
                        icon={<MedicineBoxOutlined />}
                        style={{ backgroundColor: '#4A90E2' }}
                      />
                    </div>
                    <Title level={4} className="expert-name">{expert.name}</Title>
                    <Text className="expert-title">{expert.title} · {expert.department}</Text>
                    <Paragraph className="expert-description">
                      {expert.specialties.slice(0, 3).join('、')}
                    </Paragraph>
                    <div style={{ marginTop: 12 }}>
                      <Text style={{ fontSize: 12, color: '#999' }}>
                        满意度: {expert.satisfactionRate}% | 患者数: {expert.patientCount}
                      </Text>
                    </div>
                  </Card>
                </Col>
              ))
            ) : (
              <Col span={24}>
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  暂无专家数据
                </div>
              </Col>
            )}
          </Row>
        </div>
      </section>

      {/* 统计数据区 */}
      <section className="stats-section">
        <div className="section-container">
          <Row gutter={[48, 32]}>
            {stats.map((stat, index) => (
              <Col xs={12} sm={6} key={index}>
                <div className="stat-card">
                  <div className="stat-icon" style={{ color: stat.color }}>
                    {stat.icon}
                  </div>
                  <Statistic
                    title={stat.title}
                    value={stat.value}
                    suffix={stat.suffix}
                    valueStyle={{ color: stat.color, fontSize: 36, fontWeight: 700 }}
                  />
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </section>
    </div>
  )
}

export default Home
